import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowUp, Loader2, Mic, Square } from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatRelativeTime } from "@/lib/relative-time";
import { tools } from "@/lib/tools";
import { useDictation } from "@/lib/use-dictation";

const promptInput = z.strictObject({
  prompt: z.string().min(1, "Bitte geben Sie eine Anfrage ein").max(10000),
});

export const Route = createFileRoute("/_authed/agents/")({
  component: Agents,
});

// Written as a dentist should write: insurance, the teeth, the findings the
// rules ask (erhaltungswürdig, the clinical crown, root canal, disparallel)
// and the treatment planned, so clicking one teaches what a good request
// holds; a terse one leaves mola to assume, and the dentist to correct each
// assumption in the card. The cases stay within what the verification plugin
// evaluates (statutory insurance, Zahnersatz: the FZ-RL's crowns and
// bridges), or the card says „noch nicht abgedeckt“; none names a bonus
// level, which the card asks and never takes from the message. The question
// is the other use, one the sources answer.
const examples = [
  "Gesetzlich versichert. 15 fehlt, geplant ist eine Brücke von 14 auf 16 mit Vollkronen als Anker; 14 und 16 sind erhaltungswürdig, nicht wurzelbehandelt und nicht disparallel. Außerdem braucht 36 eine Krone: erhaltungswürdig, klinische Krone weitgehend zerstört, Regelversorgung.",
  "Kassenpatientin. 24 ist erhaltungswürdig, die klinische Krone weitgehend zerstört, nicht wurzelbehandelt. Die Patientin wünscht eine vollkeramische Krone.",
  "Privatpatient, Wurzelkanalbehandlung an 36 mit drei Kanälen in einer Sitzung: welche GOZ-Positionen fallen an, und was kostet das zum 2,3-fachen Satz?",
];

// The sidebar already lists every conversation; here only a jump-back-in.
const RECENT_LIMIT = 5;

function Agents() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const request = useRef({ prompt: "", id: crypto.randomUUID() });
  const conversations = useQuery(tools.queryOptions("GET /api/conversations", {}));

  const form = useForm<z.infer<typeof promptInput>>({
    resolver: zodResolver(promptInput),
    defaultValues: { prompt: "" },
  });

  const startRun = useMutation(
    tools.mutationOptions("POST /api/conversations", {
      onSuccess: async (conversation) => {
        form.reset();
        request.current = { prompt: "", id: crypto.randomUUID() };
        // Straight to the detail view — the stream is already being written.
        await queryClient.invalidateQueries({ queryKey: tools.queryKey("GET /api/conversations") });
        await navigate({
          to: "/conversations/$conversationId",
          params: { conversationId: conversation.id },
        });
      },
    }),
  );

  // An example fills the box rather than sending: most need adapting (tooth,
  // factor, position), and a click should not silently start a paid model run.
  const fillExample = (text: string) => {
    form.setValue("prompt", text, { shouldValidate: false });
    form.clearErrors("prompt");
    const box = document.getElementById("prompt") as HTMLTextAreaElement | null;
    box?.focus();
    box?.setSelectionRange(text.length, text.length);
  };

  const dictation = useDictation((text) => {
    const current = form.getValues("prompt");
    const next = current.trim() ? `${current.trim()} ${text}` : text;
    form.setValue("prompt", next, { shouldValidate: true });
    form.clearErrors("prompt");
  });

  const now = new Date();
  const recent = conversations.data?.data.slice(0, RECENT_LIMIT) ?? [];

  return (
    <div className="mx-auto w-full max-w-2xl space-y-12 p-4 md:p-8">
      <div className="space-y-6 pt-[8vh]">
        <div className="space-y-2 text-center">
          <h1 className="font-semibold text-3xl tracking-tight md:text-4xl">
            Wie kann ich helfen?
          </h1>
          <p className="text-muted-foreground text-sm">
            Beschreiben Sie einen Fall, und mola legt den Kostenvoranschlag an. Oder fragen Sie nach
            GOZ- und BEMA-Positionen und Abrechnungsregeln.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit((values) => {
            if (startRun.isPending) return;
            if (request.current.prompt !== values.prompt)
              request.current = { prompt: values.prompt, id: crypto.randomUUID() };
            startRun.mutate({ ...values, idempotencyKey: request.current.id });
          })}
          noValidate
        >
          <div className="flex items-end gap-2 rounded-xl border bg-background p-2 transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
            <label htmlFor="prompt" className="sr-only">
              Ihre Anfrage
            </label>
            <Textarea
              id="prompt"
              submitOnEnter
              rows={2}
              className="min-h-16 border-0 px-2 py-2 focus-visible:ring-0"
              placeholder={
                dictation.state === "recording"
                  ? "Aufnahme läuft…"
                  : dictation.state === "transcribing"
                    ? "Wird erkannt…"
                    : "Fall beschreiben oder Frage stellen…"
              }
              aria-invalid={Boolean(form.formState.errors.prompt)}
              aria-describedby={form.formState.errors.prompt ? "prompt-error" : undefined}
              disabled={startRun.isPending || dictation.state !== "idle"}
              {...form.register("prompt")}
            />
            {dictation.supported && (
              <Button
                type="button"
                size="icon"
                variant={dictation.state === "recording" ? "destructive" : "outline"}
                aria-label={
                  dictation.state === "recording"
                    ? "Aufnahme beenden"
                    : dictation.state === "transcribing"
                      ? "Wird erkannt"
                      : "Diktieren"
                }
                aria-pressed={dictation.state === "recording"}
                className="size-10 shrink-0 rounded-lg"
                disabled={startRun.isPending || dictation.state === "transcribing"}
                onClick={dictation.toggle}
              >
                {dictation.state === "transcribing" ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : dictation.state === "recording" ? (
                  <Square className="size-3.5 fill-current" />
                ) : (
                  <Mic className="size-4" />
                )}
              </Button>
            )}
            <Button
              type="submit"
              size="icon"
              aria-label="Unterhaltung starten"
              className="size-10 shrink-0 rounded-lg"
              disabled={startRun.isPending || dictation.state !== "idle"}
            >
              {startRun.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-4" />
              )}
            </Button>
          </div>
          {form.formState.errors.prompt ? (
            <p id="prompt-error" role="alert" className="mt-2 text-destructive text-sm">
              {form.formState.errors.prompt.message}
            </p>
          ) : null}
          {(startRun.error || dictation.error) && (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {startRun.error?.message ?? dictation.error}
            </p>
          )}
        </form>

        <ul className="space-y-2" aria-label="Beispiele">
          {examples.map((example) => (
            <li key={example}>
              <button
                type="button"
                onClick={() => fillExample(example)}
                className="w-full rounded-lg border px-4 py-3 text-left text-muted-foreground text-sm transition-colors hover:border-foreground/25 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {example}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {recent.length ? (
        <section className="space-y-2">
          <h2 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider">
            Letzte Unterhaltungen
          </h2>
          <ul className="divide-y border-t">
            {recent.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  className="-mx-2 flex items-baseline gap-4 rounded-md px-2 py-3 text-sm hover:bg-muted"
                  to="/conversations/$conversationId"
                  params={{ conversationId: conversation.id }}
                >
                  <span className="min-w-0 flex-1 truncate">{conversation.title}</span>
                  <span className="shrink-0 text-muted-foreground text-xs">
                    {formatRelativeTime(new Date(conversation.updatedAt), now)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
